import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, inject, input } from '@angular/core';

import { MomentModule } from 'ngx-moment';

import type { Attachment } from '@core/attachments-types';
import { AttachmentItemComponent } from '@core/attachments-ui';
import { TranslatePipe } from '@core/i18n-legacy';
import { IconComponent, RtTooltip } from '@core/user-interface-legacy';
import { LinkWrapperComponent } from '@core/utils-legacy';
import type { Visibility } from '@core/visibility-types';
import { VisibilityLevel } from '@core/visibility-types';
// eslint-disable-next-line boundaries/element-types
import { ZiggeoPlayerComponent } from '@core/ziggeo-player-smart-ui'; //  --FIXME This component should be moved to smart-ui

import type { CandidateQuestionsItem } from './candidate-questions-item.interfaces';
import { QuestionKind } from '../../candidate-questions.interfaces';
import type { CandidateQuestionsModuleTranslations } from '../../translations';
import { CandidateQuestionsTranslations } from '../../translations';

@Component({
  selector: 'rt-candidate-questions-item',
  templateUrl: './candidate-questions-item.component.html',
  styleUrls: ['./candidate-questions-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-private]': 'visibility && visibility.level !== VisibilityLevel.Public',
  },
  imports: [
    IconComponent,
    RtTooltip,
    NgTemplateOutlet,
    AttachmentItemComponent,
    ZiggeoPlayerComponent,
    TranslatePipe,
    MomentModule,
    LinkWrapperComponent,
  ],
})
export class CandidateQuestionsItemComponent {
  public readonly translations = inject<CandidateQuestionsModuleTranslations>(
    CandidateQuestionsTranslations,
  );

  public readonly QuestionKind: typeof QuestionKind = QuestionKind;
  public readonly VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;

  public readonly visibilityTplRef = input<TemplateRef<any>>(undefined!);
  public readonly canManageCandidates = input<boolean>(false);
  public readonly hasBooleanFlagAnswers = input<boolean>(true);
  @Input() public data: CandidateQuestionsItem;

  public get title(): string {
    return this.data.question;
  }

  public get content(): string {
    return this.data.content!;
  }

  public get contents(): string[] {
    return this.data.contents!;
  }

  public get hasBooleanAnswer(): boolean {
    return typeof this.data.flag === 'boolean';
  }

  public get booleanAnswer(): boolean {
    return this.data.flag!;
  }

  public get kind(): string {
    return this.data.kind;
  }

  public get visibility(): Visibility {
    return this.data.visibility!;
  }

  public get visible(): boolean {
    return typeof this.data.visible === 'boolean' ? this.data.visible : true;
  }

  public get attachments(): Attachment[] {
    const attachments = this.data.attachment ? [this.data.attachment] : this.data.attachments;
    return attachments || [];
  }

  public get ratingScale(): number {
    return this.data.questionOptions!.scale === '1-5' ? 5 : 10;
  }

  public get currency(): string {
    return (this.data.questionOptions || this.data.openQuestionOptions).currency;
  }

  public get multiChoice(): string {
    return this.contents ? this.contents.filter(text => !!text).join(', ') : this.content;
  }

  public get hasContent(): boolean {
    return (
      !!this.content ||
      !!this.multiChoice ||
      this.attachments!.length > 0 ||
      this.kind === QuestionKind.Boolean ||
      this.kind === QuestionKind.Legal
    );
  }

  public attachmentsTrackBy(index: number, item: Attachment): number {
    return item!.id;
  }
}
